// Single source of truth for the canonical V1 trade list.
//
// Per master briefing Section 10 + handover Section 6, the V1 trade list is
// LOCKED at 12 entries with no "Other" option. Do not add, remove, or
// rename without updating the briefing first — these strings are stored on
// `leads.trade`, `profiles.trades`, and `quotes` rows in production.
//
// Three downstream surfaces consumed parallel copies of this list:
//   • src/components/customer/LeadForm.js          → tradeOptions (with subtypes)
//   • src/components/tradie/TradieSettings.js      → TRADE_TYPES (names only)
//   • src/components/customer/LeadFormSteps/StepTrade.js → TRADES (name + emoji)
// All three now import from here so the list cannot drift.

/** @type {ReadonlyArray<string>} */
export const TRADES = Object.freeze([
  "Painting",
  "Plumbing",
  "Electrical",
  "Solar & Battery",
  "Building",
  "Carpentry",
  "Cleaning",
  "Concreter",
  "Handyman",
  "HVAC",
  "Landscaping",
  "Roofing",
]);

/**
 * Project subtypes shown in LeadForm after a trade is picked. Keys MUST
 * match TRADES exactly.
 */
export const TRADE_PROJECT_TYPES = Object.freeze({
  Painting: ["Interior repaint", "Exterior repaint", "Rental turnover", "Touch-up and repairs", "Feature walls", "Fence / deck staining"],
  Plumbing: ["Blocked drain", "Leak investigation", "Hot water system", "Tap / toilet replacement", "Bathroom rough-in", "Gas plumbing"],
  Electrical: ["Power points / switches", "Lighting installation", "Switchboard upgrade", "Fault finding", "Smoke alarms", "Renovation electrical"],
  "Solar & Battery": ["New solar system", "Battery storage", "Solar + battery package", "System upgrade", "Inverter replacement", "Solar health check"],
  Building: ["New build", "Home extension", "Renovation", "Garage or shed", "Deck or pergola", "Knockdown rebuild"],
  Carpentry: ["Door installation", "Skirting / architraves", "Decking", "Framing", "Cabinet / shelving", "General repairs"],
  Cleaning: ["End of lease clean", "Deep clean", "Regular house clean", "Office cleaning", "Post-renovation clean", "Window cleaning"],
  Concreter: ["Driveway", "Slab or foundation", "Paths & pathways", "Pool surrounds", "Decorative concrete", "Repairs"],
  Handyman: ["General repairs", "Wall mounting", "Door / lock repairs", "Flat pack assembly", "Patch and repair", "Odd jobs"],
  HVAC: ["New air conditioner", "Ducted system", "Service & maintenance", "Repair", "Replacement unit", "Evaporative cooling"],
  Landscaping: ["Garden clean-up", "Turf installation", "Retaining wall", "Paving", "Planting / mulching", "Full yard makeover"],
  Roofing: ["Roof leak", "Gutter replacement", "Roof restoration", "Tile repairs", "Metal roof repairs", "Repointing"],
});

/** Emoji per trade for the customer-facing picker (StepTrade). */
export const TRADE_EMOJIS = Object.freeze({
  Painting: "\u{1F3A8}",
  Plumbing: "\u{1F527}",
  Electrical: "⚡",
  "Solar & Battery": "☀️",
  Building: "\u{1F3D7}️",
  Carpentry: "\u{1FA9A}",
  Cleaning: "\u{1F9F9}",
  Concreter: "\u{1F9F1}",
  Handyman: "\u{1F528}",
  HVAC: "❄️",
  Landscaping: "\u{1F33F}",
  Roofing: "\u{1F3E0}",
});
